<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import type { ManagerCallResult } from '@sysarb/sysarb-types/services/v1/organisation-node';
import SearchBar from '@/components/common/forms/SearchBar.vue';
import PopupMenuItem from '@/components/common/popup/PopupMenuItem.vue';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';
import { vInfiniteScroll } from '@vueuse/components';
import { useOrganisationNodes } from '@/composables/useOrganisationNodes';

const emit = defineEmits<{
    (e: 'change', value: string | null): void;
}>();

defineProps({
    id: {
        type: String as () => string | null,
        default: null,
        required: false
    },
    selectNone: {
        type: String,
        required: false,
        default: undefined
    },
    enableSelectNone: {
        type: Boolean,
        default: true,
        required: false
    }
});

const search = ref<string>('');
const itemsPerPage = 20;
let currentIndex = 0;

const data = ref<ManagerCallResult[]>([]);

const { possibleManagersState } = useOrganisationNodes({ initPossibleManagers: true, possibleManagersParams: {
    Search: '',
    IsManager: undefined,
    HasManagerCode: undefined
} });

const uniqueSortedManagerList = computed(() => [...new Set(possibleManagersState.data.value ?? [])].sort((a, b) => a.name.localeCompare(b.name)));
const searchedData = computed(() => uniqueSortedManagerList.value.filter((node): boolean => node.name.toLowerCase().includes(search.value.toLowerCase())));

function updateManager(node: ManagerCallResult | null): void {
    emit('change', node?.id ?? null);
}

function onSearch(): void {
    data.value = [];
    currentIndex = 0;
    onLoadMore();
}

function onLoadMore(): void {
    if (data.value.length >= searchedData.value.length) {
        return;
    }
    const list = searchedData.value;

    const newItems = list.slice(currentIndex, currentIndex + itemsPerPage);
    data.value.push(...newItems);
    currentIndex += itemsPerPage;
}

watch(() => search.value, onSearch);

</script>

<template>
    <div class="sy-manager-container">
        <div class="padding-5 padding-bottom-3">
            <SearchBar
                id="parent-node"
                v-model="search"
                :placeholder="$t('components.managerSelector.findManager')"
                :enable-icon="true"
                :autofocus="true"
            />
        </div>

        <LoadingSpinner
            v-if="possibleManagersState.isLoading.value"
            class="py-5"
            size="small"
            :loading-message="$t('components.managerSelector.loadingManagers')"
        />
        <div v-else>
            <PopupMenuItem
                v-if="enableSelectNone"
                v-close-popper
                :text="selectNone === undefined ? $t('components.managerSelector.noManager') : selectNone"
                :selected="id == null"
                @click="updateManager(null)"
            />
            <div v-infinite-scroll="onLoadMore" class="sy-node-list">
                <PopupMenuItem
                    v-for="(node, index) of data"
                    :key="index"
                    v-close-popper
                    :text="`${node.name} (${node.socialSecurityNumber})`"
                    :selected="id == node.id"
                    @click="updateManager(node)"
                />
            </div>
            <SText is="p" v-if="data.length === 0" class="padding-5">{{ $t('components.managerSelector.noMatchingManager') }}</SText>
        </div>
    </div>
</template>

<style lang="scss" scoped>

.sy-node-list {
    max-height: 400px;
    overflow: auto;
}
</style>
